import React from "react";
import Image from "next/image";
import { Package } from "components/ContentfulRenderer/ContentfulPackages/types";
import { formatCurrency, formatCurrencyForA11y } from "utils/currency";
import { useWebTranslation } from "translations";
import { Typography } from "ui-kit/Typography";
import styles from "./InsurancePackageV2.module.css";
import Colors from "ui-kit/colors";
import { ProductEnum } from "../../../../../__generated__/globalTypes";
import classNames from "classnames";
import { Icon } from "ui-kit";
import classnames from "classnames";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { getCurrentDraftForInsurance } from "components/Signup/store/selectors/animals";
import { VisuallyHidden } from "@reach/visually-hidden";
import { Insurance } from "components/Signup/store/state/types";

const LimitAddonButton = ({
  selected,
  onClick,
  label,
  ariaLabel,
}: {
  selected: boolean;
  onClick: VoidFunction;
  label: string;
  ariaLabel: string;
}) => {
  return (
    <button
      style={{
        backgroundColor: selected ? "transparent" : "white",
        flex: 1,
        border: "1px solid grey",
        borderColor: selected ? Colors.blue300 : Colors.grey300,
        padding: 8,
        borderRadius: 20,
        color: selected ? Colors.blue300 : "black",
      }}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();

        onClick();
      }}
      aria-pressed={selected}
    >
      <Typography variant="body3">
        <strong>{label}</strong>
      </Typography>
    </button>
  );
};

const getUsps = (planName: string, product: ProductEnum) => {
  switch (planName) {
    case "small":
      return [
        { title: "copy_exp.common.1", value: true },
        { title: "copy_exp.common.2", value: true },
        { title: "copy_exp.common.3", value: true },
        { title: "copy_exp.common.4", value: true },
        {
          title: `copy_exp.common.5_${product}`,
          value: true,
        },
        { title: "copy_exp.common.6", value: true },
      ];
    case "medium":
      return [
        { title: "copy_exp.common.8", value: true },
        { title: "copy_exp.common.1", value: true },
        { title: "copy_exp.common.2", value: true },
        { title: "copy_exp.common.3", value: true },
        { title: "copy_exp.common.4", value: true },
        {
          title: `copy_exp.common.5_${product}`,
          value: true,
        },
        { title: "copy_exp.common.6", value: true },
      ];
    case "large":
      return [
        { title: "copy_exp.common.8", value: true },
        { title: "copy_exp.common.1", value: true },
        { title: "copy_exp.common.2", value: true },
        { title: "copy_exp.common.3", value: true },
        { title: "copy_exp.common.4", value: true },
        {
          title: `copy_exp.common.5_${product}`,
          value: true,
        },
        { title: "copy_exp.common.6", value: true },
      ];
    default:
      return [];
  }
};

export const InsurancePackage = ({
  pack,
  selectedLimitAddon,
  onPressLimitAddon,
  isActive,
  onCompare,
  onPress,
  style,
  product,
  forDisplay = false,
  insurance,
}: {
  isActive: boolean;
  pack: Package;
  selectedLimitAddon?: number;
  onPress?: VoidFunction;
  onPressLimitAddon?: (limit: number) => void;
  onCompare: () => void;
  style?: React.CSSProperties;
  product: ProductEnum;
  forDisplay?: boolean;
  insurance?: Insurance;
}) => {
  const { t } = useWebTranslation();

  const usps = getUsps(pack.name, product)
    .map((item) => item.title)
    .filter((item) => {
      if (!forDisplay) {
        if (item === "copy_exp.common.2" || item === "copy_exp.common.3") {
          return false;
        }
      }

      return true;
    });

  return (
    <section
      role="group"
      aria-label={t("global.insurance_package") + " " + t(pack.title)}
      style={{ display: "flex", flexDirection: "column" }}
    >
      {pack.popular && (
        <Typography
          variant="body4"
          className={styles.popularLabel}
          style={{
            backgroundColor: pack.popularColor ?? Colors.sun300,
            borderColor: isActive ? Colors.blue300 : Colors.grey200,
          }}
        >
          <strong>{t("global.most_popular")}</strong>
        </Typography>
      )}

      <div
        className={classNames(
          styles.container,
          isActive && styles.containerActive,
          forDisplay && styles.containerForDisplay,
          pack.popular && styles.containerPopular
        )}
        onClick={onPress}
        style={style}
      >
        <div
          className={classNames(
            styles.titleContainer,
            pack.popular && styles.titleContainerPopular
          )}
          style={{ backgroundColor: pack.color }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {!forDisplay && (
              <div
                className={classnames(
                  styles.bullet,
                  isActive && styles.bulletActive
                )}
              />
            )}
            <Typography variant="h3" component="h2">
              {t(pack.title)}
            </Typography>
          </div>
          <Image src={pack.img} alt="" width={60} height={60} />
        </div>
        <div className={styles.limitContainer}>
          {pack.limitAddons ? (
            forDisplay ? (
              <>
                <Typography variant="body3">
                  <strong>{t("insurance_package.cover_limit")}</strong>
                </Typography>
                <Typography variant="body1">
                  <strong>
                    {t("insurance_package.cover_limit_range", {
                      low: formatCurrency(pack.limitAddons[0] / 100),
                      high: formatCurrency(
                        pack.limitAddons[pack.limitAddons.length - 1] / 100
                      ),
                    })}
                  </strong>
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body3">
                  <strong>{t("insurance_package.choose_limit")}</strong>
                </Typography>
                <div style={{ display: "flex", gap: 4 }}>
                  {pack.limitAddons.map((l) => {
                    if (!insurance) {
                      return null;
                    }

                    const draft = getCurrentDraftForInsurance({
                      ...insurance,
                      limit: l,
                    });

                    const amountToPay = getAmountToPay({
                      ...draft,
                      billingInterval: insurance.billingInterval,
                    });

                    return (
                      <LimitAddonButton
                        key={l}
                        selected={selectedLimitAddon === l}
                        onClick={() => {
                          onPressLimitAddon && onPressLimitAddon(l);
                        }}
                        label={formatCurrency(l / 100)}
                        ariaLabel={`${t(pack.title)}, ${formatCurrencyForA11y(
                          l / 100
                        )}, ${t("global.price")}: ${formatCurrencyForA11y(
                          amountToPay.new
                        )}`}
                      />
                    );
                  })}
                </div>
              </>
            )
          ) : (
            <>
              <Typography variant="body3">
                <strong>{t("insurance_package.cover_limit")}</strong>
              </Typography>
              <Typography variant="body1">
                <strong>{formatCurrency(pack.limit / 100)}</strong>
              </Typography>

              <VisuallyHidden>
                <button onClick={onPress} aria-pressed={isActive}>
                  {`${t("global.select")} ${t("global.insurance_package")} ${t(
                    pack.title
                  )}`}
                </button>
              </VisuallyHidden>
            </>
          )}
        </div>
        <ul className={styles.benefitsContainer}>
          {Object.values(usps).map((value) => (
            <li key={value} className={styles.benefit}>
              <div>
                <Icon
                  name="checkmark"
                  size="L"
                  color={Colors.green200}
                  ariaHidden
                />
              </div>
              {t(value)}
            </li>
          ))}
        </ul>
        <div className={styles.footer}>
          <button
            onClick={(e) => {
              onCompare && onCompare();

              e?.stopPropagation();
            }}
            className={styles.compareButton}
          >
            <Typography variant="body3" color="blue300">
              <strong>{t("insurance_packages.all_benefits")}</strong>
            </Typography>
          </button>
        </div>
      </div>
    </section>
  );
};
